import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { Wishlist } from '@theme/wishlist';

/**
 * A custom element that displays a wishlist icon with a live item-count badge.
 * Unlike the cart bubble, the count is always read synchronously from
 * localStorage (no server round-trip), so no bfcache/session-storage
 * reconciliation is needed — every render is authoritative.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wishlistBubble - The wishlist bubble element.
 * @property {HTMLElement} wishlistBubbleText - The wishlist bubble text element.
 * @property {HTMLElement} wishlistBubbleCount - The wishlist bubble count element.
 *
 * @extends {Component<Refs>}
 */
class WishlistIcon extends Component {
  requiredRefs = ['wishlistBubble', 'wishlistBubbleText', 'wishlistBubbleCount'];

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.wishlistUpdate, this.onWishlistUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.renderBubble(Wishlist.count());
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.wishlistUpdate, this.onWishlistUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  onPageShow = () => {
    this.renderBubble(Wishlist.count());
  };

  /**
   * @param {CustomEvent} event
   */
  onWishlistUpdate = (event) => {
    const itemCount = event.detail?.data?.itemCount ?? Wishlist.count();
    this.renderBubble(itemCount);
  };

  /**
   * @param {number} itemCount
   */
  renderBubble = (itemCount) => {
    this.refs.wishlistBubbleCount.textContent = itemCount < 100 ? String(itemCount) : '';
    this.refs.wishlistBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.wishlistBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.classList.toggle('header-actions__wishlist-icon--has-items', itemCount > 0);
  };
}

if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIcon);
}
